import { useEffect } from 'react';
import { useThemeCustom } from '../hooks/useThemeCustom';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import PropTypes from 'prop-types';

/**
 * Provider que aplica o tema customizado ao Material-UI
 * Converte as configurações do tema JSON em um tema do Material-UI
 */
export default function ThemeApplier({ children, baseTheme }) {
  const { currentTheme, getColor } = useThemeCustom();

  // Cria tema Material-UI baseado nas configurações do tema customizado
  const muiTheme = createTheme({
    ...baseTheme,
    palette: {
      ...baseTheme.palette,
      mode: 'light', // Força modo light independente da preferência do sistema
      primary: {
        main: getColor('primary'),
        dark: getColor('primaryDark'),
        light: getColor('primaryLight'),
      },
      secondary: {
        main: getColor('secondary'),
        dark: getColor('secondaryDark'),
        light: getColor('secondaryLight'),
      },
      background: {
        default: getColor('background'),
        paper: getColor('backgroundPaper'),
      },
      text: {
        primary: getColor('text'),
        secondary: getColor('textSecondary'),
        disabled: getColor('textDisabled'),
      },
      success: {
        main: getColor('success'),
      },
      error: {
        main: getColor('error'),
      },
      warning: {
        main: getColor('warning'),
      },
      info: {
        main: getColor('info'),
      },
      divider: getColor('divider'),
    },
    typography: {
      ...baseTheme.typography,
      fontFamily: currentTheme.fonts.fontFamily,
    },
  });

  // Aplica CSS variables globais
  useEffect(() => {
    const root = document.documentElement;

    // Aplica cores
    Object.entries(currentTheme.colors).forEach(([key, value]) => {
      root.style.setProperty(`--theme-color-${key}`, value);
    });

    // Aplica fontes
    Object.entries(currentTheme.fonts).forEach(([key, value]) => {
      root.style.setProperty(`--theme-font-${key}`, value);
    });

    // Aplica estilos customizados se existirem
    if (currentTheme.customStyles) {
      Object.entries(currentTheme.customStyles).forEach(([key, value]) => {
        root.style.setProperty(`--theme-custom-${key}`, value);
      });
    }
  }, [currentTheme]);

  return <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>;
}

ThemeApplier.propTypes = {
  children: PropTypes.node.isRequired,
  baseTheme: PropTypes.object.isRequired,
};
